"use client";

import { useSectionReveal } from "./useSectionReveal";

interface ComparisonRow {
  feature: string;
  printed: string | boolean;
  photo: string | boolean;
  video: string | boolean;
  myshaadilink: string | boolean;
}

const ROWS: ComparisonRow[] = [
  {
    feature: "Cost per invite",
    printed: "₹50–200",
    photo: "₹1,000",
    video: "₹4,000",
    myshaadilink: "₹1,999",
  },
  {
    feature: "RSVP tracking",
    printed: false,
    photo: false,
    video: false,
    myshaadilink: true,
  },
  {
    feature: "Photo gallery",
    printed: false,
    photo: false,
    video: false,
    myshaadilink: true,
  },
  {
    feature: "Live countdown",
    printed: false,
    photo: false,
    video: false,
    myshaadilink: true,
  },
  {
    feature: "Venue map & directions",
    printed: false,
    photo: false,
    video: false,
    myshaadilink: true,
  },
  {
    feature: "Background music",
    printed: false,
    photo: false,
    video: true,
    myshaadilink: true,
  },
  {
    feature: "Easy to share",
    printed: false,
    photo: true,
    video: true,
    myshaadilink: true,
  },
  {
    feature: "Eco-friendly",
    printed: false,
    photo: true,
    video: true,
    myshaadilink: true,
  },
  {
    feature: "Premium look & feel",
    printed: true,
    photo: true,
    video: true,
    myshaadilink: true,
  },
  {
    feature: "Personalized guest names",
    printed: true,
    photo: false,
    video: false,
    myshaadilink: true,
  },
];

function CellValue({ value }: { value: string | boolean }) {
  if (typeof value === "string") {
    return <span className="iv-compare__text">{value}</span>;
  }
  if (value) {
    return (
      <svg
        className="iv-compare__check"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <circle cx="10" cy="10" r="10" fill="currentColor" opacity="0.12" />
        <path
          d="M6 10.5l2.5 2.5 5.5-5.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg
      className="iv-compare__cross"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M7 7l6 6M13 7l-6 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function ComparisonTable() {
  const sectionRef = useSectionReveal<HTMLElement>();

  return (
    <section
      className="iv-section iv-compare"
      id="compare"
      ref={sectionRef}
    >
      <div className="iv-section__inner">
        <div className="iv-heading">
          <h2 className="iv-heading__title iv-font-display">
            How We Compare
          </h2>
          <p className="iv-heading__sub">
            See why digital invitations from MyShaadhi Link are the smarter,
            more beautiful choice for your wedding.
          </p>
        </div>

        <div className="iv-compare__table iv-compare__table--4col">
          {/* Header */}
          <div className="iv-compare__header">
            <div className="iv-compare__feature-col" />
            <div className="iv-compare__col">
              {/* Printed Cards icon */}
              <span className="iv-compare__col-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
              </span>
              <span className="iv-compare__col-label">Printed Cards</span>
            </div>
            <div className="iv-compare__col">
              {/* Photo Invite icon */}
              <span className="iv-compare__col-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </span>
              <span className="iv-compare__col-label">Photo Invite</span>
            </div>
            <div className="iv-compare__col">
              {/* Video Invite icon */}
              <span className="iv-compare__col-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
              </span>
              <span className="iv-compare__col-label">Video Invite</span>
            </div>
            <div className="iv-compare__col iv-compare__col--highlight">
              {/* MyShaadhi Link star icon */}
              <span className="iv-compare__col-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" />
                </svg>
              </span>
              <span className="iv-compare__col-label">MyShaadhi Link</span>
            </div>
          </div>

          {/* Rows */}
          {ROWS.map((row) => (
            <div className="iv-compare__row" key={row.feature}>
              <div className="iv-compare__feature-col">{row.feature}</div>
              <div className="iv-compare__cell">
                <CellValue value={row.printed} />
              </div>
              <div className="iv-compare__cell">
                <CellValue value={row.photo} />
              </div>
              <div className="iv-compare__cell">
                <CellValue value={row.video} />
              </div>
              <div className="iv-compare__cell iv-compare__cell--highlight">
                <CellValue value={row.myshaadilink} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
